import { useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  addDoc,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getDatabase } from 'firebase/database';
import { getPerformance, initializePerformance } from 'firebase/performance';
import { getFunctions, httpsCallable } from 'firebase/functions';
import errorLogger, { handleFirebasePerformanceError } from '../utils/errorLogger';
import toast from 'react-hot-toast';

const CACHE_TTL = 300000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const BATCH_SIZE = 100;
const CONCURRENT_REQUESTS = 10;

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const firestore = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);
const database = getDatabase(firebaseApp);
const functions = getFunctions(firebaseApp);

let analytics = null;
let performance = null;

if (process.env.REACT_APP_ENABLE_PERFORMANCE === 'true') {
  try {
    // Auto Web Vitals/DOM instrumentation puts Tailwind class strings into trace attributes and
    // throws performance/invalid attribute value. Manual monitorPerformance() traces are still fine.
    performance = initializePerformance(firebaseApp, {
      instrumentationEnabled: false,
      dataCollectionEnabled: false,
    });
  } catch (error) {
    try {
      performance = getPerformance(firebaseApp);
      performance.instrumentationEnabled = false;
      performance.dataCollectionEnabled = false;
    } catch (_) {}
  }
}

const monitorPerformance = (traceName, traceFunction) => {
  if (performance) {
    try {
      const sanitizedTraceName = traceName
        .replace(/[^\w\s\-_]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 100);

      const trace = performance.trace(sanitizedTraceName);
      trace.start();
      traceFunction();
      trace.stop();
    } catch (error) {
      handleFirebasePerformanceError(error);
      traceFunction();
    }
  } else {
    traceFunction();
  }
};

const clientsCollection = collection(firestore, 'clients');
const applicationsCollection = collection(firestore, 'applications');
const moderatorAssignmentsCollection = collection(firestore, 'moderator_assignments');
const legalConsentsCollection = collection(firestore, 'legal_consents');
const STORAGE_PATHS = {
  ID_DOCUMENTS: 'id_documents',
  BUSINESS_PLANS: 'business_plans',
  FINANCIAL_STATEMENTS: 'financial_statements',
  TAX_CLEARANCE: 'tax_clearance',
};
const FEEDBACK_COLLECTION = 'feedback';
const REPORTS_COLLECTION = 'weeklyReports';

const registerUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error) {
    errorLogger.logError(error, {
      type: 'firebase_auth',
      action: 'register_user',
      email: email,
    });
    throw error;
  }
};

const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    errorLogger.logError(error, {
      type: 'firebase_auth',
      action: 'login_user',
      email: email,
    });
    throw error;
  }
};

const resetPassword = async email => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw error;
  }
};

const logoutUser = async () => {
  try {
    if (window.caches) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    localStorage.clear();
    sessionStorage.clear();

    await signOut(auth);

    window.location.href = '/landing';

    window.history.pushState(null, '', '/landing');
    window.addEventListener('popstate', function () {
      window.location.href = '/landing';
    });
  } catch (error) {
    window.location.href = '/landing';
  }
};

const uploadFile = async (file, path) => {
  try {
    const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    throw error;
  }
};

const deleteFile = async url => {
  try {
    const fileRef = ref(storage, url);
    await deleteObject(fileRef);
  } catch (error) {
    throw error;
  }
};

const createClient = async (clientData, uid) => {
  try {
    const data = {
      ...clientData,
      firstname: clientData.firstname || '',
      lastname: clientData.lastname || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const docRef = doc(clientsCollection, uid);
    await setDoc(docRef, data);
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

const createApplication = async (applicationData, uid) => {
  try {
    const docRef = doc(applicationsCollection, uid);
    await setDoc(docRef, {
      ...applicationData,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    try {
      await autoAssignCoachAndModerator(docRef.id);
    } catch (assignmentError) {}

    return docRef.id;
  } catch (error) {
    throw error;
  }
};

const updateApplication = async (applicationId, data) => {
  try {
    const docRef = doc(applicationsCollection, applicationId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw error;
  }
};

const getApplication = async applicationId => {
  try {
    const docRef = doc(applicationsCollection, applicationId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

const getAllApplications = async () => {
  try {
    const snapshot = await getDocs(applicationsCollection);
    const applications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return applications;
  } catch (error) {
    throw error;
  }
};

const getActiveModerators = async () => {
  try {
    const snapshot = await getDocs(
      query(collection(firestore, 'moderator'), where('status', '==', 'active'))
    );
    const moderators = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return moderators;
  } catch (error) {
    throw error;
  }
};

const getRandomActiveModerator = async () => {
  try {
    const activeModerators = await getActiveModerators();
    if (activeModerators.length === 0) {
      throw new Error('No active moderators available');
    }

    const moderatorAssignments = await Promise.all(
      activeModerators.map(async moderator => ({
        ...moderator,
        clientCount: await getModeratorClientCount(moderator.id),
      }))
    );

    const minClientCount = Math.min(...moderatorAssignments.map(m => m.clientCount));

    const eligibleModerators = moderatorAssignments.filter(m => m.clientCount === minClientCount);

    const randomIndex = Math.floor(Math.random() * eligibleModerators.length);
    return eligibleModerators[randomIndex];
  } catch (error) {
    throw error;
  }
};

const getModeratorAssignments = async () => {
  try {
    const snapshot = await getDocs(moderatorAssignmentsCollection);
    const assignments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return assignments;
  } catch (error) {
    throw error;
  }
};

// Coach assignment functions
const getActiveCoaches = async () => {
  try {
    const snapshot = await getDocs(
      query(collection(firestore, 'coaches'), where('status', '==', 'active'))
    );
    const coaches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return coaches;
  } catch (error) {
    throw error;
  }
};

const getRandomActiveCoach = async () => {
  try {
    const activeCoaches = await getActiveCoaches();
    if (activeCoaches.length === 0) {
      throw new Error('No active coaches available');
    }

    const coachAssignments = await Promise.all(
      activeCoaches.map(async coach => ({
        ...coach,
        clientCount: await getCoachClientCount(coach.id),
      }))
    );

    const minClientCount = Math.min(...coachAssignments.map(c => c.clientCount));
    const eligibleCoaches = coachAssignments.filter(c => c.clientCount === minClientCount);

    const randomIndex = Math.floor(Math.random() * eligibleCoaches.length);
    return eligibleCoaches[randomIndex];
  } catch (error) {
    throw error;
  }
};

const getCoachClientCount = async coachId => {
  try {
    const snapshot = await getDocs(
      query(collection(firestore, 'clients'), where('assignedCoachEmail', '==', coachId))
    );
    return snapshot.size;
  } catch (error) {
    return 0;
  }
};

const assignCoachToApplication = async (applicationId, coachId, coachName, coachEmail) => {
  try {
    const appRef = doc(applicationsCollection, applicationId);
    await updateDoc(appRef, {
      assignedCoachName: coachName,
      assignedCoachEmail: coachEmail,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw error;
  }
};

const assignModeratorToApplication = async (
  applicationId,
  moderatorId,
  moderatorName,
  moderatorEmail
) => {
  try {
    const appRef = doc(applicationsCollection, applicationId);
    await updateDoc(appRef, {
      assigned: moderatorName,
      assignedEmail: moderatorEmail,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw error;
  }
};

const autoAssignCoachAndModerator = async applicationId => {
  try {
    const [assignedCoach, assignedModerator] = await Promise.all([
      getRandomActiveCoach(),
      getRandomActiveModerator(),
    ]);

    await Promise.all([
      assignCoachToApplication(
        applicationId,
        assignedCoach.id,
        `${assignedCoach.firstname} ${assignedCoach.lastname}`.trim(),
        assignedCoach.email
      ),
      assignModeratorToApplication(
        applicationId,
        assignedModerator.id,
        `${assignedModerator.firstname} ${assignedModerator.lastname}`.trim(),
        assignedModerator.email
      ),
    ]);
    return { assignedCoach, assignedModerator };
  } catch (error) {
    throw error;
  }
};

const createModeratorAssignment = async (applicationId, moderatorId, moderatorName) => {
  try {
    const docRef = doc(moderatorAssignmentsCollection);
    await setDoc(docRef, {
      applicationId,
      moderatorId,
      moderatorName,
      assignedAt: serverTimestamp(),
      status: 'active',
    });
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

const getModeratorClientCount = async moderatorId => {
  try {
    const snapshot = await getDocs(
      query(
        moderatorAssignmentsCollection,
        where('moderatorId', '==', moderatorId),
        where('status', '==', 'active')
      )
    );
    return snapshot.size;
  } catch (error) {
    throw error;
  }
};

const addFeedback = async feedbackData => {
  try {
    const feedbackRef = await addDoc(collection(firestore, FEEDBACK_COLLECTION), {
      ...feedbackData,
      createdAt: new Date().toISOString(),
    });
    return feedbackRef.id;
  } catch (error) {
    throw error;
  }
};

const getFeedbackByReportId = async reportId => {
  try {
    const q = query(
      collection(firestore, FEEDBACK_COLLECTION),
      where('reportId', '==', reportId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    throw error;
  }
};
const getFeedbackByUserId = async userId => {
  try {
    const q = query(
      collection(firestore, FEEDBACK_COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    throw error;
  }
};

const addWeeklyReport = async reportData => {
  try {
    const attachmentUrls = await Promise.all(
      reportData.attachments.map(async file => {
        const storageRef = ref(storage, `reports/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      })
    );
    const reportRef = await addDoc(collection(firestore, REPORTS_COLLECTION), {
      ...reportData,
      attachments: attachmentUrls,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    return reportRef.id;
  } catch (error) {
    throw error;
  }
};

const updateWeeklyReport = async (reportId, reportData) => {
  try {
    const reportRef = doc(firestore, REPORTS_COLLECTION, reportId);
    await updateDoc(reportRef, {
      ...reportData,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    throw error;
  }
};

const deleteWeeklyReport = async reportId => {
  try {
    await deleteDoc(doc(firestore, REPORTS_COLLECTION, reportId));
  } catch (error) {
    throw error;
  }
};

const getWeeklyReports = async (email = null) => {
  try {
    let q = collection(firestore, REPORTS_COLLECTION);

    if (email) {
      q = query(q, where('email', '==', email));
    }

    const querySnapshot = await getDocs(q);
    const reports = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    return reports.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } catch (error) {
    throw error;
  }
};
const getWeeklyReportById = async reportId => {
  try {
    const reportRef = doc(firestore, REPORTS_COLLECTION, reportId);
    const reportDoc = await getDoc(reportRef);

    if (reportDoc.exists()) {
      return {
        id: reportDoc.id,
        ...reportDoc.data(),
      };
    }
    return null;
  } catch (error) {
    throw error;
  }
};

const cache = new Map();
const cacheTimestamps = new Map();

let isConnected = true;

const getCachedData = key => {
  const timestamp = cacheTimestamps.get(key);
  if (timestamp && Date.now() - timestamp < CACHE_TTL) {
    return cache.get(key);
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, data);
  cacheTimestamps.set(key, Date.now());
};

const clearCache = () => {
  cache.clear();
  cacheTimestamps.clear();
};

const retryWithBackoff = async (operation, retries = MAX_RETRIES) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await operation();
    } catch (error) {
      if (i === retries - 1) throw error;

      const delay = RETRY_DELAY * Math.pow(2, i);
      toast.warn(`Firebase operation failed, retrying in ${delay}ms:`, error);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const firestoreService = {
  async getDocument(collection, docId, useCache = true) {
    const cacheKey = `${collection}_${docId}`;

    if (useCache) {
      const cached = getCachedData(cacheKey);
      if (cached) return cached;
    }

    const operation = async () => {
      const docRef = doc(firestore, collection, docId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        if (useCache) setCachedData(cacheKey, data);
        return data;
      }
      return null;
    };

    return retryWithBackoff(operation);
  },

  async getCollection(collection, queryConstraints = [], useCache = true) {
    const cacheKey = `${collection}_${JSON.stringify(queryConstraints)}`;

    if (useCache) {
      const cached = getCachedData(cacheKey);
      if (cached) return cached;
    }

    const operation = async () => {
      const colRef = collection(firestore, collection);
      const q = query(colRef, ...queryConstraints);
      const snapshot = await getDocs(q);

      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (useCache) setCachedData(cacheKey, data);
      return data;
    };

    return retryWithBackoff(operation);
  },

  async setDocument(collection, docId, data) {
    const operation = async () => {
      const docRef = doc(firestore, collection, docId);

      const documentData = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      await setDoc(docRef, documentData);

      const cacheKey = `${collection}_${docId}`;
      setCachedData(cacheKey, { id: docId, ...documentData });

      return docId;
    };

    return retryWithBackoff(operation);
  },

  async updateDocument(collection, docId, data) {
    const operation = async () => {
      const docRef = doc(firestore, collection, docId);

      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);

      const cacheKey = `${collection}_${docId}`;
      cache.delete(cacheKey);
      cacheTimestamps.delete(cacheKey);

      return docId;
    };

    return retryWithBackoff(operation);
  },

  async batchSet(operations) {
    const operation = async () => {
      const { writeBatch } = await import('firebase/firestore');
      const batch = writeBatch(firestore);

      operations.forEach(({ collection, docId, data }) => {
        const docRef = doc(firestore, collection, docId);
        const documentData = {
          ...data,
          updatedAt: serverTimestamp(),
        };
        batch.set(docRef, documentData);
      });

      await batch.commit();

      operations.forEach(({ collection, docId, data }) => {
        const cacheKey = `${collection}_${docId}`;
        setCachedData(cacheKey, { id: docId, ...data });
      });
    };

    return retryWithBackoff(operation);
  },

  clearCache,
};

const getUserLegalConsents = async userId => {
  try {
    const ref = doc(legalConsentsCollection, userId);
    const snap = await getDoc(ref);
    if (snap.exists()) return { id: snap.id, ...snap.data() };
    return null;
  } catch (error) {
    throw error;
  }
};

const setUserLegalConsents = async (userId, consents) => {
  try {
    const ref = doc(legalConsentsCollection, userId);
    const payload = {
      ...consents,
      updatedAt: serverTimestamp(),
      createdAt: consents?.createdAt || serverTimestamp(),
    };
    await setDoc(ref, payload, { merge: true });
    return true;
  } catch (error) {
    throw error;
  }
};

const authService = {
  async signIn(email, password) {
    const operation = async () => {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      return await signInWithEmailAndPassword(auth, email, password);
    };

    return retryWithBackoff(operation);
  },

  async signUp(email, password) {
    const operation = async () => {
      const { createUserWithEmailAndPassword } = await import('firebase/auth');
      return await createUserWithEmailAndPassword(auth, email, password);
    };

    return retryWithBackoff(operation);
  },

  async signOut() {
    const operation = async () => {
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
      clearCache();
    };

    return retryWithBackoff(operation);
  },

  async resetPassword(email) {
    const operation = async () => {
      const { sendPasswordResetEmail } = await import('firebase/auth');
      return await sendPasswordResetEmail(auth, email);
    };

    return retryWithBackoff(operation);
  },
};

const storageService = {
  async uploadFile(file, path) {
    const operation = async () => {
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      return await getDownloadURL(snapshot.ref);
    };

    return retryWithBackoff(operation);
  },

  async deleteFile(url) {
    const operation = async () => {
      const { ref, deleteObject } = await import('firebase/storage');
      const fileRef = ref(storage, url);
      return await deleteObject(fileRef);
    };

    return retryWithBackoff(operation);
  },
};

export const useFirestore = collectionName => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchData = async (constraints = [], pageSize = 10) => {
    try {
      setLoading(true);
      const collectionRef = collection(firestore, collectionName);
      let q = query(collectionRef, ...constraints, limit(pageSize));

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const lastVisible = snapshot.docs[snapshot.docs.length - 1];
      setLastDoc(lastVisible);

      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setData(prev => [...prev, ...items]);
      setHasMore(snapshot.docs.length === pageSize);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCount = async (constraints = []) => {
    try {
      const collectionRef = collection(firestore, collectionName);
      const q = query(collectionRef, ...constraints);
      const snapshot = await getCountFromServer(q);
      return snapshot.data().count;
    } catch (err) {
      return 0;
    }
  };

  const reset = () => {
    setData([]);
    setLastDoc(null);
    setHasMore(true);
  };

  return {
    data,
    loading,
    error,
    hasMore,
    fetchData,
    getCount,
    reset,
  };
};

const updateUserDocument = async (userId, data, collection = 'clients') => {
  try {
    const sanitizedData = Object.keys(data).reduce((acc, key) => {
      const value = data[key];
      if (value !== null && value !== undefined) {
        if (typeof value === 'string') {
          acc[key] = value.replace(/[^\w\s\-.,!?@#$%&*()+=]/g, '');
        } else if (Array.isArray(value)) {
          acc[key] = value.map(item => {
            if (typeof item === 'string') {
              return item.replace(/[^\w\s\-.,!?@#$%&*()+=]/g, '');
            } else if (typeof item === 'object' && item !== null) {
              return Object.keys(item).reduce((objAcc, objKey) => {
                const objValue = item[objKey];
                if (typeof objValue === 'string') {
                  objAcc[objKey] = objValue.replace(/[^\w\s\-.,!?@#$%&*()+=]/g, '');
                } else {
                  objAcc[objKey] = objValue;
                }
                return objAcc;
              }, {});
            }
            return item;
          });
        } else if (typeof value === 'object' && value !== null) {
          acc[key] = Object.keys(value).reduce((objAcc, objKey) => {
            const objValue = value[objKey];
            if (typeof objValue === 'string') {
              objAcc[objKey] = objValue.replace(/[^\w\s\-.,!?@#$%&*()+=]/g, '');
            } else {
              objAcc[objKey] = objValue;
            }
            return objAcc;
          }, {});
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    const userRef = doc(firestore, collection, userId);
    await updateDoc(userRef, {
      ...sanitizedData,
      updatedAt: new Date(),
    });
    return true;
  } catch (error) {
    throw error;
  }
};

const updateEducationData = async (userId, educationData, collection = 'clients') => {
  try {
    const validatedEducation = educationData.map(edu => ({
      degree: edu.degree || '',
      institution: edu.institution || '',
      year: edu.year || '',
      description: edu.description || '',
      gpa: edu.gpa || '',
      fieldOfStudy: edu.fieldOfStudy || '',
      createdAt: edu.createdAt || new Date(),
      updatedAt: new Date(),
    }));

    const userRef = doc(firestore, collection, userId);
    await updateDoc(userRef, {
      education: validatedEducation,
      updatedAt: new Date(),
    });
    return true;
  } catch (error) {
    throw error;
  }
};

const updateCertificationsData = async (userId, certificationsData, collection = 'clients') => {
  try {
    const validatedCertifications = certificationsData.map(cert => ({
      title: cert.title || '',
      issuer: cert.issuer || '',
      date: cert.date || '',
      description: cert.description || '',
      credentialId: cert.credentialId || '',
      expiryDate: cert.expiryDate || '',
      url: cert.url || '',
      createdAt: cert.createdAt || new Date(),
      updatedAt: new Date(),
    }));

    const userRef = doc(firestore, collection, userId);
    await updateDoc(userRef, {
      certifications: validatedCertifications,
      updatedAt: new Date(),
    });
    return true;
  } catch (error) {
    throw error;
  }
};

const updateProjectsData = async (userId, projectsData, collection = 'clients') => {
  try {
    const validatedProjects = projectsData.map(project => ({
      name: project.name || '',
      status: project.status || 'In Progress',
      progress: parseInt(project.progress) || 0,
      description: project.description || '',
      createdAt: project.createdAt || new Date(),
      updatedAt: new Date(),
    }));

    const userRef = doc(firestore, collection, userId);
    await updateDoc(userRef, {
      projects: validatedProjects,
      updatedAt: new Date(),
    });
    return true;
  } catch (error) {
    throw error;
  }
};

const updateWorkExperienceData = async (userId, workExperienceData, collection = 'clients') => {
  try {
    const validatedExperience = workExperienceData.map(exp => ({
      title: exp.title || '',
      company: exp.company || '',
      startDate: exp.startDate || '',
      endDate: exp.endDate || '',
      isCurrent: exp.isCurrent || false,
      description: exp.description || '',
      duration: exp.duration || '',
      period: exp.period || '',
      createdAt: exp.createdAt || new Date(),
      updatedAt: new Date(),
    }));

    const userRef = doc(firestore, collection, userId);
    await updateDoc(userRef, {
      workExperience: validatedExperience,
      updatedAt: new Date(),
    });
    return true;
  } catch (error) {
    throw error;
  }
};

const updateBioData = async (userId, bioData, collection = 'clients') => {
  try {
    const validatedBio = bioData.trim();
    if (validatedBio.length > 1000) {
      throw new Error('Bio is too long. Please keep it under 1000 characters.');
    }

    const userRef = doc(firestore, collection, userId);
    await updateDoc(userRef, {
      bio: validatedBio,
      updatedAt: new Date(),
    });
    return true;
  } catch (error) {
    throw error;
  }
};

const createUserByAdmin = async (userData, userType) => {
  try {
    const createUserFunction = httpsCallable(functions, 'createUserByAdmin');
    const result = await createUserFunction({ userData, userType });
    return result.data;
  } catch (error) {
    throw error;
  }
};

const updateUserByAdmin = async (userId, userData, userType) => {
  try {
    const updateUserFunction = httpsCallable(functions, 'updateUserByAdmin');
    const result = await updateUserFunction({ userId, userData, userType });
    return result.data;
  } catch (error) {
    throw error;
  }
};

const deleteUserByAdmin = async (userId, userType) => {
  try {
    const deleteUserFunction = httpsCallable(functions, 'deleteUserByAdmin');
    const result = await deleteUserFunction({ userId, userType });
    return result.data;
  } catch (error) {
    throw error;
  }
};

const getUsersWithFilters = async (collectionName, filters = {}, pageSize = 20, cursor = null) => {
  try {
    const collectionRef = collection(firestore, collectionName);
    let queryConstraints = [];

    if (filters.role) {
      queryConstraints.push(where('role', '==', filters.role));
    }
    if (filters.status) {
      queryConstraints.push(where('status', '==', filters.status));
    }
    if (filters.businessStage) {
      queryConstraints.push(where('businessStage', '==', filters.businessStage));
    }
    if (filters.cohortGroup) {
      queryConstraints.push(where('cohortGroup', '==', filters.cohortGroup));
    }
    if (filters.organization) {
      queryConstraints.push(where('organization', '==', filters.organization));
    }

    queryConstraints.push(orderBy('updatedAt', 'desc'));
    queryConstraints.push(limit(pageSize));

    if (cursor) {
      queryConstraints.push(startAfter(cursor));
    }

    const q = query(collectionRef, ...queryConstraints);
    const snapshot = await getDocs(q);

    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    const countQuery = query(collectionRef, ...queryConstraints.slice(0, -2));
    const countSnapshot = await getCountFromServer(countQuery);
    const total = countSnapshot.data().count;

    return {
      users,
      hasMore: snapshot.docs.length === pageSize,
      cursor: snapshot.docs[snapshot.docs.length - 1] || null,
      total,
    };
  } catch (error) {
    throw error;
  }
};

const updateUserBusinessStage = async (
  collectionName,
  userId,
  newStage,
  reason = 'Manual update'
) => {
  try {
    const userRef = doc(firestore, collectionName, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('User not found');
    }

    const userData = userSnap.data();
    const previousStage = userData.businessStage || 'Idea';

    await updateDoc(userRef, {
      businessStage: newStage,
      updatedAt: serverTimestamp(),
    });

    await addDoc(collection(firestore, 'stageAuditLogs'), {
      userId,
      userEmail: userData.email,
      collection: collectionName,
      previousStage,
      newStage,
      changedBy: {
        uid: 'admin',
        email: 'admin@example.com',
      },
      reason,
      affectedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    throw error;
  }
};

const uploadUserImage = async (file, userId, imageType) => {
  try {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only JPEG, PNG, and WebP are allowed');
    }

    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB');
    }

    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}_${imageType}.${fileExtension}`;
    const storagePath = `${imageType === 'profile' ? 'profilePictures' : 'banners'}/${userId}/${fileName}`;

    const storageRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    const userRef = doc(firestore, 'clients', userId);
    const updateData = {};
    updateData[imageType === 'profile' ? 'profilePicture' : 'bannerPhoto'] = downloadURL;
    updateData.updatedAt = serverTimestamp();

    await updateDoc(userRef, updateData);

    await addDoc(collection(firestore, 'mediaAuditLogs'), {
      userId,
      kind: imageType,
      path: storagePath,
      size: file.size,
      type: file.type,
      url: downloadURL,
      uploadedAt: serverTimestamp(),
      uploadedBy: {
        uid: 'admin',
        email: 'admin@example.com',
      },
    });

    return { success: true, url: downloadURL };
  } catch (error) {
    throw error;
  }
};

const getAuditLogs = async (userId, logType = 'stage') => {
  try {
    const collectionName = logType === 'stage' ? 'stageAuditLogs' : 'mediaAuditLogs';
    const auditLogRef = collection(firestore, collectionName);
    const q = query(
      auditLogRef,
      where('userId', '==', userId),
      orderBy(logType === 'stage' ? 'affectedAt' : 'uploadedAt', 'desc'),
      limit(50)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    throw error;
  }
};

export {
  auth,
  firestore,
  storage,
  database,
  analytics,
  performance,
  firebaseApp,
  functions,
  deleteWeeklyReport,
  authService,
  firestoreService,
  addWeeklyReport,
  storageService,
  getWeeklyReports,
  updateWeeklyReport,
  getWeeklyReportById,
  STORAGE_PATHS,
  addFeedback,
  getFeedbackByReportId,
  getFeedbackByUserId,
  registerUser,
  loginUser,
  resetPassword,
  logoutUser,
  uploadFile,
  deleteFile,
  createClient,
  createApplication,
  updateApplication,
  getApplication,
  getAllApplications,
  onAuthStateChanged,
  getActiveModerators,
  getRandomActiveModerator,
  assignModeratorToApplication,
  getModeratorClientCount,
  getModeratorAssignments,
  getActiveCoaches,
  getRandomActiveCoach,
  assignCoachToApplication,
  autoAssignCoachAndModerator,
  updateUserDocument,
  updateEducationData,
  updateCertificationsData,
  updateProjectsData,
  updateWorkExperienceData,
  updateBioData,
  createUserByAdmin,
  updateUserByAdmin,
  deleteUserByAdmin,
  getUserLegalConsents,
  setUserLegalConsents,
  getUsersWithFilters,
  updateUserBusinessStage,
  uploadUserImage,
  getAuditLogs,
};
